import React, { useState, useRef, useEffect } from 'react';
import { TelnyxRTC } from '@telnyx/webrtc';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, addDoc, updateDoc, serverTimestamp, setDoc, deleteDoc, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/20/solid';
import { Cog6ToothIcon, ChevronRightIcon, ChevronLeftIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiSpeedometer, GiEchoRipples } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import { useLocation, Link, useNavigate } from 'react-router-dom';
import './VoiceDialer.css';
import { voiceDialerConfig } from '../config/voiceDialerConfig';
import InternationalCountryModal from './InternationalCountryModal';
import CountryFlag from './CountryFlag';
import { 
  formatInternationalPhoneNumber, 
  detectCountryFromPhoneNumber,
  getCountryByCode 
} from '../utils/countryUtils';

// Function to get navigation items - adding world call
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function VoiceDialer2() {
  const [phone, setPhone] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [call, setCall] = useState(null);
  const [isCalling, setIsCalling] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [timer, setTimer] = useState(0);
  const [telnyxClient, setTelnyxClient] = useState(null);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [user, setUser] = useState(null);
  const [isMessagingEnabled, setIsMessagingEnabled] = useState(false);
  const [showMessagingDialog, setShowMessagingDialog] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isActivatingMessaging, setIsActivatingMessaging] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [hasTriedToCall, setHasTriedToCall] = useState(false);
  const [currentCallHistoryId, setCurrentCallHistoryId] = useState(null);
  const [callHistory, setCallHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [audioContextActivated, setAudioContextActivated] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({ code: 'US', name: 'United States', dialingCode: '+1' });
  
  const timerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = getFilteredNavigation(location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  // Emergency and premium numbers to block
  const blockedNumbers = [
    '911', '112', '999', '000', '108', '119', // Emergency numbers
    '1900', '1976', // US premium numbers
    '+1900', '+1976',
    // Add more patterns as needed
  ];

  // Check if mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Call history tracking functions
  const createCallHistoryRecord = async (destinationNumber, callerNumber, telnyxCallId = null) => {
    if (!user?.uid) return null;
    
    try {
      const now = new Date().toISOString();
      const callRecord = {
        destinationNumber: destinationNumber,
        callerNumber: callerNumber,
        callDirection: 'outbound',
        callStatus: 'initiated',
        startTime: serverTimestamp(),
        endTime: null,
        connectedTime: null,
        duration: 0,
        telnyxCallId: telnyxCallId,
        events: [{
          type: 'call_initiated',
          timestamp: now,
          details: { destinationNumber, callerNumber }
        }]
      };
      
      // Use a temporary ID first, then update with Telnyx call ID when available
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const docId = telnyxCallId || tempId;
      
      const docRef = doc(db, 'userCallHistory', user.uid, 'calls', docId);
      await setDoc(docRef, callRecord);
      console.log('📊 Call history record created with ID:', docId);
      return docId;
    } catch (error) {
      console.error('❌ Error creating call history record:', error);
      return null;
    }
  };

  const updateCallHistoryWithTelnyxId = async (tempId, telnyxCallId) => {
    if (!tempId || !telnyxCallId || !user?.uid) return telnyxCallId;
    
    try {
      // If we used a temp ID, move the document to use the Telnyx call ID
      if (tempId.startsWith('temp_')) {
        const tempDocRef = doc(db, 'userCallHistory', user.uid, 'calls', tempId);
        const newDocRef = doc(db, 'userCallHistory', user.uid, 'calls', telnyxCallId);
        
        // Get the temp document
        const tempDoc = await getDoc(tempDocRef);
        if (tempDoc.exists()) {
          // Create new document with Telnyx call ID
          await setDoc(newDocRef, {
            ...tempDoc.data(),
            telnyxCallId: telnyxCallId
          });
          
          // Delete the temp document
          await deleteDoc(tempDocRef);
          console.log('📊 Call history moved from temp ID to Telnyx ID:', telnyxCallId);
        }
        return telnyxCallId;
      }
      
      // If already using Telnyx ID, just update the telnyxCallId field
      const docRef = doc(db, 'userCallHistory', user.uid, 'calls', tempId);
      await updateDoc(docRef, { telnyxCallId: telnyxCallId });
      return tempId;
    } catch (error) {
      console.error('❌ Error updating call history with Telnyx ID:', error);
      return tempId;
    }
  };

  const updateCallHistoryRecord = async (callId, updates) => {
    if (!callId || !user?.uid) return;
    
    try {
      const docRef = doc(db, 'userCallHistory', user.uid, 'calls', callId);
      await updateDoc(docRef, updates);
      console.log('📊 Call history updated:', callId, updates);
    } catch (error) {
      console.error('❌ Error updating call history:', error);
    }
  };

  const addCallEvent = async (callId, eventType, details = {}) => {
    if (!callId || !user?.uid) return;
    
    try {
      const docRef = doc(db, 'userCallHistory', user.uid, 'calls', callId);
      const event = {
        type: eventType,
        timestamp: new Date().toISOString(),
        details: details
      };
      
      // First get current events, then append new event
      const currentDoc = await getDoc(docRef);
      if (currentDoc.exists()) {
        const currentEvents = currentDoc.data().events || [];
        await updateDoc(docRef, {
          events: [...currentEvents, event]
        });
        console.log('📊 Call event added:', eventType, details);
      }
    } catch (error) {
      console.error('❌ Error adding call event:', error);
    }
  };

  // Check if number should be blocked
  const isBlockedNumber = (number) => {
    const cleanNumber = number.replace(/[\s\-()+ ]/g, '');
    return blockedNumbers.some(blocked => 
      cleanNumber.startsWith(blocked) || 
      cleanNumber === blocked ||
      (blocked.startsWith('+') && number.startsWith(blocked))
    );
  };

  // Load user and subscription data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await fetchSubscriptionData(currentUser.uid);
        await fetchCallHistory(currentUser.uid);
      } else {
        setUser(null);
        setIsMessagingEnabled(false);
        setSubscription(null);
        setCallHistory([]);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Check for pending call number from Overview page
  useEffect(() => {
    const pendingNumber = localStorage.getItem('pendingCallNumber');
    if (pendingNumber) {
      // Detect country from the full number
      const detectedCountry = detectCountryFromPhoneNumber(pendingNumber);
      if (detectedCountry) {
        setSelectedCountry(detectedCountry);
        // Remove the country code from the display
        const cleanNumber = pendingNumber.replace(new RegExp(`^\\${detectedCountry.dialingCode}`), '');
        setPhone(cleanNumber);
      } else {
        // Fallback: assume US number and clean +1
        const cleanNumber = pendingNumber.replace(/^\+1/, '');
        setPhone(cleanNumber);
      }
      localStorage.removeItem('pendingCallNumber'); // Clean up after use
      console.log('📞 Loaded pending call number from Overview:', pendingNumber);
    }
  }, []);

  // Debug button state periodically
  useEffect(() => {
    const debugInterval = setInterval(() => {
      if (phone) { // Only log when there's actually a phone number
        console.log('🔍 Button State Debug:', {
          phone: phone,
          phoneLength: phone?.length,
          connectionStatus: connectionStatus,
          isConnected: connectionStatus === 'connected',
          hasPhone: !!phone,
          isBlockedNumber: isBlockedNumber(phone || ''),
          isMessagingEnabled: isMessagingEnabled,
          isCalling: isCalling,
          isConnecting: isConnecting,
          buttonDisabled: !phone || connectionStatus !== 'connected' || isBlockedNumber(phone || ''),
          telnyxClient: !!telnyxClient,
          user: !!user
        });
      }
    }, 5000); // Log every 5 seconds

    return () => clearInterval(debugInterval);
  }, [phone, connectionStatus, isMessagingEnabled, isCalling, isConnecting, telnyxClient, user]);

  // Fetch call history from Firebase
  const fetchCallHistory = async (uid) => {
    if (!uid) return;
    
    try {
      setLoadingHistory(true);
      const callsRef = collection(db, 'userCallHistory', uid, 'calls');
      const q = query(callsRef, orderBy('startTime', 'desc'), limit(50));
      
      // Set up real-time listener for call history
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const calls = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          calls.push({
            id: doc.id,
            ...data,
            // Convert Firestore timestamp to Date for easier handling
            startTime: data.startTime?.toDate ? data.startTime.toDate() : new Date(data.startTime),
            endTime: data.endTime?.toDate ? data.endTime.toDate() : (data.endTime ? new Date(data.endTime) : null),
            connectedTime: data.connectedTime?.toDate ? data.connectedTime.toDate() : (data.connectedTime ? new Date(data.connectedTime) : null)
          });
        });
        setCallHistory(calls);
        setLoadingHistory(false);
      });
      
      // Store the unsubscribe function to clean up later
      return unsubscribe;
    } catch (error) {
      console.error('❌ Error fetching call history:', error);
      setLoadingHistory(false);
    }
  };

  // Format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get call status display text
  const getCallStatusText = (status) => {
    switch (status) {
      case 'answered': return 'Connected';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      case 'busy': return 'Busy';
      case 'no-answer': return 'No Answer';
      case 'initiated': return 'Initiated';
      default: return status || 'Unknown';
    }
  };

  // Get call status color
  const getCallStatusColor = (status) => {
    switch (status) {
      case 'answered':
      case 'completed': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'busy': return 'text-yellow-400';
      case 'no-answer': return 'text-orange-400';
      case 'initiated': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  // Navigate to contacts page with phone number search
  const navigateToContact = (phoneNumber) => {
    // Store the phone number in localStorage for ContactsList to pick up
    localStorage.setItem('contactSearchNumber', phoneNumber);
    navigate('/contacts');
  };

  // Handle clicking on a call history item
  const handleCallHistoryClick = (destinationNumber) => {
    // Detect country from the full number
    const detectedCountry = detectCountryFromPhoneNumber(destinationNumber);
    if (detectedCountry) {
      setSelectedCountry(detectedCountry);
      // Remove the country code from the display
      const cleanNumber = destinationNumber.replace(new RegExp(`^\\${detectedCountry.dialingCode}`), '');
      setPhone(cleanNumber);
    } else {
      // Fallback: assume US number
    const cleanNumber = destinationNumber.replace(/^\+1/, '');
    setPhone(cleanNumber);
    }
  };

  // Function to fetch user subscription data
  const fetchSubscriptionData = async (uid) => {
    try {
      const subscriptionRef = doc(db, 'userSubscriptions', uid);
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        console.log("Subscription data:", subscriptionData);
        setSubscription(subscriptionData);
        
        // Check if messaging is enabled using modern bundle system
        let messagingEnabled = false;
        if (subscriptionData.subscriptionStatus === 'active') {
          // Check modern bundle data or fallback to legacy fields
          if (subscriptionData.isMessaging === true || 
              (subscriptionData.messagingPlanActive === true && 
               subscriptionData.isMessagingEnabled === true)) {
            messagingEnabled = true;
          }
        }
        setIsMessagingEnabled(messagingEnabled);
      } else {
        console.log("No subscription document found for user");
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    }
  };

  // Function to activate messaging by upgrading to messaging plan
  const activateMessaging = async () => {
    setIsActivatingMessaging(true);
    
    try {
      // Get the current subscription data
      if (!user?.uid) {
        throw new Error('User not authenticated');
      }
      
      // Check if user was on trial before upgrade
      const wasOnTrial = subscription?.subscriptionStatus === 'trialing' || subscription?.trialEndsAt;
      
      // Call API to upgrade to messaging plan
      const response = await fetch('/api/upgrade-to-messaging', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: user.uid
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upgrade to messaging plan');
      }
      
      const upgradeData = await response.json();
      console.log('Successfully upgraded to messaging plan:', upgradeData);
      
      // Show success message
      const message = wasOnTrial 
        ? 'Your free trial has ended successfully. Your Relay Premium + Messaging subscription is now active!'
        : 'Your Relay Premium + Messaging subscription has been activated successfully!';
      
      setSuccessMessage(message);
      setShowSuccessMessage(true);
      
      // Auto-hide success message after 4 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 4000);
      
      // Refresh subscription data to get the new subscription details
      await fetchSubscriptionData(user.uid);
    } catch (error) {
      console.error('Error upgrading to messaging plan:', error);
      alert(`There was an error upgrading to the messaging plan: ${error.message}`);
    } finally {
      setIsActivatingMessaging(false);
      setShowMessagingDialog(false);
    }
  };

  // Initialize Telnyx client exactly as per documentation
  useEffect(() => {
    const initTelnyxClient = async () => {
      try {
        console.log('🔌 Initializing Telnyx client...');

        // Initialize the client exactly as per documentation
        const client = new TelnyxRTC({
          // Use your Connection credentials
          login: process.env.REACT_APP_TELNYX_SIP_USERNAME || 'userraph71943',
          password: process.env.REACT_APP_TELNYX_SIP_PASSWORD || '5JMx0uRw',
          // Alternative: Use JWT token authentication (uncomment if you have a token)
          // login_token: 'YOUR_JWT_TOKEN_HERE',
          // DO NOT set remoteElement in constructor - set it after element exists
          // Enable audio debugging
          debug: true,
          // Ensure audio is enabled
          audioConstraints: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        console.log('🔊 Telnyx client created with remoteElement:', client.remoteElement);
        console.log('🔊 SDK version:', client.constructor.version || 'unknown');

        // IMMEDIATELY set remoteElement after client creation
        // Create a plain HTML audio element (not React-managed) to avoid circular references
        let remoteAudioElement = document.getElementById('remoteMedia');
        if (remoteAudioElement) {
          // Remove the React-managed element if it exists
          remoteAudioElement.remove();
        }
        
        // Create a new plain HTML audio element without React fiber properties
        remoteAudioElement = document.createElement('audio');
        remoteAudioElement.id = 'remoteMedia';
        remoteAudioElement.autoplay = true;
        remoteAudioElement.playsInline = true;
        remoteAudioElement.style.display = 'none';
        
        // Append to body so it exists in DOM
        document.body.appendChild(remoteAudioElement);
        console.log('🔊 Plain HTML audio element created:', remoteAudioElement.tagName);
        
        // Set remoteElement IMMEDIATELY after client creation (critical timing)
        client.remoteElement = 'remoteMedia';
        console.log('🔊 Remote audio element ID set to: remoteMedia');
        console.log('🔊 Client remoteElement after setting:', typeof client.remoteElement === 'string' ? client.remoteElement : 'DOM_ELEMENT');
        console.log('🔊 Client remoteElement type:', typeof client.remoteElement);

        // Attach event listeners exactly as per documentation
        client
          .on('telnyx.ready', () => {
            console.log('✅ Telnyx ready to call');
            setConnectionStatus('connected');
          })
          .on('telnyx.error', (error) => {
            console.error('❌ Telnyx error:', error);
            setConnectionStatus('error');
            alert(`Telnyx Error: ${error.message || 'Connection failed'}`);
          })
          // Events are fired on both session and call updates
          .on('telnyx.notification', (notification) => {
            console.log('🔔 Telnyx notification:', notification.type, notification);
            
            if (notification.type === 'callUpdate') {
              const activeCall = notification.call;
              console.log('📞 Call update:', activeCall.state, 'for call ID:', activeCall.id);
              
              setCall(activeCall);
              
              // Handle call state changes and track events
              switch (activeCall.state) {
                case 'ringing':
                  console.log('📞 Call is ringing');
                  // Track ringing event using Telnyx call ID
                  addCallEvent(activeCall.id, 'call_ringing', { 
                    telnyxCallId: activeCall.id,
                    direction: activeCall.direction 
                  });
                  updateCallHistoryRecord(activeCall.id, {
                    telnyxCallId: activeCall.id,
                    callStatus: 'ringing'
                  });
                  // Update current call history ID to use Telnyx ID
                  setCurrentCallHistoryId(activeCall.id);
                  
                  if (activeCall.direction === 'incoming') {
                    // Incoming call - could auto-answer or show UI
                    console.log('📞 Incoming call from:', activeCall.callerName || activeCall.callerNumber);
                    // For now, we'll auto-answer (you might want to show UI instead)
                    // activeCall.answer();
                  }
                  setIsConnecting(false);
                  setIsCalling(true);
                  break;
                case 'active':
                  console.log('📞 Call is active');
                  
                  // Debug audio stream
                  const audioElement = document.getElementById('remoteMedia');
                  if (audioElement) {
                    console.log('🔊 Audio element found, checking stream...');
                    console.log('🔊 Audio element src:', audioElement.src);
                    console.log('🔊 Audio element srcObject:', audioElement.srcObject);
                    console.log('🔊 Audio element volume:', audioElement.volume);
                    console.log('🔊 Audio element muted:', audioElement.muted);
                    console.log('🔊 Audio element ready state:', audioElement.readyState);
                    
                    // Force audio play if it's not playing
                    if (audioElement.paused) {
                      audioElement.play().then(() => {
                        console.log('🔊 Audio playback started successfully');
                      }).catch(error => {
                        console.error('🔊 Failed to start audio playback:', error);
                      });
                    }
                  } else {
                    console.error('🔊 Audio element not found!');
                  }
                  
                  // Track call answered/connected event using Telnyx call ID
                  addCallEvent(activeCall.id, 'call_answered', { 
                    telnyxCallId: activeCall.id,
                    connectedAt: new Date().toISOString()
                  });
                  updateCallHistoryRecord(activeCall.id, {
                    callStatus: 'active',
                    connectedTime: serverTimestamp()
                  });
                  // Update current call history ID to use Telnyx ID
                  setCurrentCallHistoryId(activeCall.id);
                  
                  setIsConnecting(false);
                  setIsCalling(true);
                  // Start the timer
                  if (timerRef.current) {
                    clearInterval(timerRef.current);
                  }
                  timerRef.current = setInterval(() => {
                    setTimer(prev => prev + 1);
                  }, 1000);
                  break;
                case 'hangup':
                case 'destroy':
                  console.log('📞 Call ended');
                  // Track call ended event
                  const endReason = activeCall.cause || 'normal_hangup';
                  const finalDurationSeconds = timer; // Already in seconds
                  
                  // Use the Telnyx call ID as the document ID
                  const finalCallId = activeCall.id;
                  
                  addCallEvent(finalCallId, 'call_ended', { 
                    telnyxCallId: activeCall.id,
                    endReason: endReason,
                    duration: finalDurationSeconds,
                    endedAt: new Date().toISOString()
                  });
                  
                  // Determine final call status
                  let finalStatus = 'completed';
                  if (endReason.includes('NO_ANSWER') || endReason.includes('TIMEOUT')) {
                    finalStatus = 'no_answer';
                  } else if (endReason.includes('BUSY')) {
                    finalStatus = 'busy';
                  } else if (endReason.includes('CANCEL')) {
                    finalStatus = 'cancelled';
                  } else if (finalDurationSeconds === 0) {
                    finalStatus = 'failed';
                  }
                  
                  // Final update with complete call data
                  updateCallHistoryRecord(finalCallId, {
                    callStatus: finalStatus,
                    endTime: serverTimestamp(),
                    duration: finalDurationSeconds,
                    endReason: endReason,
                    causeCode: activeCall.causeCode || null,
                    sipCode: activeCall.sipCode || null,
                    gotAnswer: activeCall.gotAnswer || false,
                    gotEarly: activeCall.gotEarly || false
                  });
                  
                  setIsCalling(false);
                  setIsConnecting(false);
                  setCall(null);
                  setTimer(0);
                  setCurrentCallHistoryId(null);
                  if (timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                  }
                  break;
                case 'held':
                  console.log('📞 Call is on hold');
                  addCallEvent(activeCall.id, 'call_held', { 
                    telnyxCallId: activeCall.id 
                  });
                  break;
                default:
                  console.log('📞 Call state:', activeCall.state);
                  addCallEvent(activeCall.id, 'call_state_change', { 
                    telnyxCallId: activeCall.id,
                    newState: activeCall.state 
                  });
              }
            }
          });

        setTelnyxClient(client);
        
        // Request microphone permission first
        try {
          console.log('🎤 Requesting microphone permission...');
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('✅ Microphone permission granted');
          setMicrophoneEnabled(true);
          // Stop the stream immediately as we only needed permission
          stream.getTracks().forEach(track => track.stop());
        } catch (micError) {
          console.error('❌ Microphone permission denied:', micError);
          setMicrophoneEnabled(false);
          alert('Microphone access is required for voice calls. Please allow microphone access and refresh the page.');
          return;
        }
        
        // Wait a moment for DOM to be ready before connecting
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Connect and login
        console.log('🔌 Connecting to Telnyx...');
        await client.connect();
        console.log('🔌 Connected to Telnyx');

      } catch (error) {
        console.error('❌ Failed to initialize Telnyx:', error);
        setConnectionStatus('error');
        alert(`Failed to connect: ${error.message}`);
      }
    };

    initTelnyxClient();

    // Cleanup on unmount
    return () => {
      if (call) {
        try {
          call.hangup();
        } catch (error) {
          console.error('Error hanging up call during cleanup:', error);
        }
      }
      if (telnyxClient) {
        try {
          telnyxClient.disconnect();
        } catch (error) {
          console.error('Error disconnecting Telnyx client:', error);
        }
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Make call function following documentation
  const handleCall = async () => {
    console.log('🎯 Call button clicked! Starting handleCall function...');
    console.log('🎯 Phone number:', phone);
    console.log('🎯 Telnyx client:', !!telnyxClient);
    console.log('🎯 Connection status:', connectionStatus);
    console.log('🎯 Current call:', !!call);
    console.log('🎯 Is calling:', isCalling);
    console.log('🎯 Is connecting:', isConnecting);
    console.log('🎯 Is messaging enabled:', isMessagingEnabled);
    console.log('🎯 User:', !!user);
    console.log('🎯 Subscription:', subscription);
    
    // Activate audio context on user interaction
    console.log('🎯 Step 1: About to activate audio context...');
    try {
      await activateAudioContext();
      console.log('🎯 Step 2: Audio context activated successfully');
    } catch (error) {
      console.error('❌ Error activating audio context:', error);
      // Continue anyway, don't let this block the call
    }
    
    if (!telnyxClient || !phone) {
      console.log('❌ Early return: Missing telnyxClient or phone');
      alert('Please enter a phone number and ensure Telnyx connection is ready');
      return;
    }
    console.log('🎯 Step 3: TelnyxClient and phone check passed');

    if (connectionStatus !== 'connected') {
      console.log('❌ Early return: Connection not ready. Status:', connectionStatus);
      alert('Telnyx connection not ready. Status: ' + connectionStatus);
      return;
    }
    console.log('🎯 Step 4: Connection status check passed');

    // Prevent multiple calls
    if (call || isCalling || isConnecting) {
      console.log('❌ Early return: Call already in progress');
      alert('A call is already in progress');
      return;
    }
    console.log('🎯 Step 5: Multiple call prevention check passed');

    // Check for blocked numbers
    if (isBlockedNumber(phone)) {
      console.log('❌ Early return: Blocked number');
      alert('This number cannot be called. Emergency and premium numbers are blocked.');
      return;
    }
    console.log('🎯 Step 6: Blocked number check passed');

    // Check Premium + Messaging eligibility for ALL calls (same as trace page)
    // TEMPORARY: Feature enforcement is disabled - keeping check for analytics but not blocking calls
    if (!isMessagingEnabled) {
      console.log('❌ User not eligible for messaging but enforcement is temporarily disabled');
      // setShowMessagingDialog(true);
      // return;
    } else {
      console.log('🎯 Step 7: Messaging eligibility check passed');
    }

    console.log('✅ All checks passed, proceeding with call...');

    try {
      setIsConnecting(true);
      console.log('📞 Starting call to:', phone);

      // Format phone number to E.164 format using selected country
      let destinationNumber;
      if (phone.startsWith('+')) {
        // Already has a country code
        destinationNumber = phone;
      } else {
        // Add the selected country's dialing code
        destinationNumber = `${selectedCountry.dialingCode}${phone}`;
      }
      const callerNumber = process.env.REACT_APP_TELNYX_CALLER_NUMBER || '+13239967539';

      console.log('📞 Calling:', destinationNumber, 'from:', callerNumber);

      // Create call history record first with temp ID
      const tempHistoryId = await createCallHistoryRecord(destinationNumber, callerNumber);
      setCurrentCallHistoryId(tempHistoryId);

      // Create call exactly as per documentation
      const newCall = telnyxClient.newCall({
        // Destination is required and can be a phone number or SIP URI
        destinationNumber: destinationNumber,
        callerNumber: callerNumber,
        // Optional parameters from official docs
        audio: true,
        video: false
      });

      console.log('📞 Call created:', newCall);
      setCall(newCall);

      // Update call history with Telnyx call ID and track creation
      if (tempHistoryId && newCall.id) {
        const finalCallId = await updateCallHistoryWithTelnyxId(tempHistoryId, newCall.id);
        setCurrentCallHistoryId(finalCallId);
        
        addCallEvent(finalCallId, 'call_created', { 
          telnyxCallId: newCall.id,
          destinationNumber,
          callerNumber 
        });
      }

    } catch (error) {
      console.error('❌ Failed to start call:', error);
      setIsConnecting(false);
      
      // Track call failure
      if (currentCallHistoryId) {
        addCallEvent(currentCallHistoryId, 'call_failed', { 
          error: error.message,
          failureReason: 'call_creation_failed'
        });
        updateCallHistoryRecord(currentCallHistoryId, {
          callStatus: 'failed',
          endTime: serverTimestamp(),
          endReason: 'call_creation_failed'
        });
        setCurrentCallHistoryId(null);
      }
      
      alert('Failed to start call: ' + error.message);
    }
  };

  // Hang up function
  const handleHangUp = () => {
    // Track manual hangup event before ending call
    if (currentCallHistoryId) {
      addCallEvent(currentCallHistoryId, 'manual_hangup', { 
        telnyxCallId: call?.id,
        duration: timer,
        hangupBy: 'user'
      });
    }
    
    // Reset UI state immediately
    setIsCalling(false);
    setIsConnecting(false);
    setTimer(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    // Then try to hangup the call
    if (call) {
      try {
        // Hangup or reject an incoming call
        call.hangup();
      } catch (error) {
        console.error('Error hanging up call:', error);
        // Don't show error to user as UI is already reset
      }
    }
    
    // Clear call reference last
    setCall(null);
  };

  // DTMF function for sending digits
  const sendDTMF = (digit) => {
    if (call && isCalling) {
      try {
        // Send digits and keypresses
        call.dtmf(digit);
        console.log('📞 Sent DTMF:', digit);
      } catch (error) {
        console.error('Error sending DTMF:', error);
      }
    }
  };

  // Format timer
  const formatTime = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'error': return 'Connection Error';
      default: return 'Disconnected';
    }
  };

  const addNumber = (digit) => {
    // Activate audio context on user interaction
    activateAudioContext();
    
    if (isCalling) {
      // Send DTMF if in call
      sendDTMF(digit);
    } else {
      // Add to phone number if not in call
      setPhone(prev => prev + digit);
    }
  };

  const removeLastDigit = () => {
    if (!isCalling) {
      setPhone(prev => prev.slice(0, -1));
    }
  };

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    
    // If there's already a number entered, reformat it with the new country code
    if (phone && phone.trim()) {
      const formattedNumber = formatInternationalPhoneNumber(phone, country.code);
      // Remove the country code from the display since we show it separately
      const digitsOnly = formattedNumber.replace(/^\+\d+/, '');
      setPhone(digitsOnly);
    }
  };

  // Auto-detect country from phone number
  useEffect(() => {
    if (phone && phone.startsWith('+')) {
      const detectedCountry = detectCountryFromPhoneNumber(phone);
      if (detectedCountry && (!selectedCountry || selectedCountry.code !== detectedCountry.code)) {
        setSelectedCountry(detectedCountry);
      }
    }
  }, [phone, selectedCountry]);

  // Dial pad numbers
  const dialPadNumbers = [
    { num: '1', letters: '' },
    { num: '2', letters: 'ABC' },
    { num: '3', letters: 'DEF' },
    { num: '4', letters: 'GHI' },
    { num: '5', letters: 'JKL' },
    { num: '6', letters: 'MNO' },
    { num: '7', letters: 'PQRS' },
    { num: '8', letters: 'TUV' },
    { num: '9', letters: 'WXYZ' },
    { num: '*', letters: '' },
    { num: '0', letters: '+' },
    { num: '#', letters: '' },
  ];

  // Function to activate audio context on user interaction
  const activateAudioContext = async () => {
    if (audioContextActivated) return;
    
    try {
      console.log('🔊 Activating audio context...');
      
      // Create and resume audio context with timeout (for browser autoplay policies)
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const audioContext = new AudioContext();
        if (audioContext.state === 'suspended') {
          console.log('🔊 Audio context suspended, resuming...');
          // Add timeout to prevent hanging
          const resumePromise = audioContext.resume();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Audio context resume timeout')), 2000)
          );
          
          await Promise.race([resumePromise, timeoutPromise]);
          console.log('🔊 Audio context resumed successfully');
        }
        
        setAudioContextActivated(true);
        console.log('🔊 Audio context activation completed');
      }
    } catch (error) {
      console.error('🔊 Error activating audio context (continuing anyway):', error);
      // Set as activated anyway to prevent hanging
      setAudioContextActivated(true);
    }
  };

  return (
    <>
      <div className="flex bg-gray-900 min-h-screen text-white">
        {/* Mobile sidebar - Liquid Glass */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component - Liquid Glass */}
              <div 
                className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex h-16 shrink-0 items-center justify-center px-4">
                  <img alt="Relay" src="/images/logowhite.png" className="h-8 w-auto" />
                    </div>
                <nav className="flex flex-1 flex-col px-4">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                      <ul role="list" className="space-y-3">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                              className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon 
                                aria-hidden="true" 
                                className={classNames(
                                  "h-6 w-6 shrink-0 transition-colors duration-200",
                                  item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                                )}
                              />
                              <span className={classNames(
                                "transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    <li className="mt-auto mb-4">
                    <Link
                      to={settingsNavItem.href}
                        className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <settingsNavItem.icon 
                          aria-hidden="true" 
                      className={classNames(
                            "h-6 w-6 shrink-0 transition-colors duration-200",
                            settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                          )} 
                        />
                        <span className={classNames(
                          "transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                      {settingsNavItem.name}
                        </span>
                    </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Desktop sidebar - Liquid Glass Collapsible */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
        }`}>
          <div className="relative">
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
                height: 'calc(100vh - 2rem)'
              }}
            >
              {/* Header with logo */}
              <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <img
                  alt="Relay"
                  src="/images/logowhite.png"
                  className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
                />
              </div>

              <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="space-y-3">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={classNames(
                              'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                              sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                            )}
                            title={!sidebarExpanded ? item.name : undefined}
                    >
                            <item.icon 
                              aria-hidden="true" 
                              className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )} 
                            />
                            {sidebarExpanded && (
                              <span className={classNames(
                                "transition-opacity duration-300",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                            )}
                    </Link>
                  </li>
                ))}
              </ul>
                  </li>
                  <li className="mt-auto mb-4">
              <Link
                to={settingsNavItem.href}
                className={classNames(
                        'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                        sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                      )}
                      title={!sidebarExpanded ? settingsNavItem.name : undefined}
              >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      {sidebarExpanded && (
                        <span className={classNames(
                          "transition-opacity duration-300",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      )}
              </Link>
                  </li>
                </ul>
          </nav>
            </div>

            {/* Right border expand/collapse area */}
            <div 
              className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              style={{ zIndex: 60 }}
            >
              {/* Hover indicator */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
                {sidebarExpanded ? (
                  <ChevronLeftIcon className="h-4 w-4 text-white/70" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-white/70" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 min-h-screen transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`} style={{background: 'linear-gradient(to bottom right, #1a0b2e, #16213e, #0f0f23)'}}>
          {/* Mobile Menu Button - Floating */}
          <button 
            type="button" 
            onClick={() => setSidebarOpen(true)} 
            className="fixed top-4 left-4 z-50 lg:hidden bg-white/10 backdrop-blur-md rounded-full p-3 text-white hover:bg-white/20 transition-all border border-white/20"
            style={{
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>

          {/* Main Content - Beautiful smartphone design with call history */}
          <div className="flex items-start justify-center min-h-screen p-4 py-8 md:items-center md:py-4">
            <div className="flex items-center justify-center gap-8 w-full max-w-6xl">
              
              {/* Smartphone Container with Liquid Glass Effect */}
              <div 
                className={`relative w-full max-w-sm bg-black/20 backdrop-blur-2xl rounded-[3rem] border border-white/10 shadow-2xl ${isMobile ? 'overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20' : 'overflow-y-auto overflow-x-hidden phone-container-desktop'}`}
                style={{
                  aspectRatio: '9/19.5',
                  minHeight: '600px',
                  maxHeight: isMobile ? 'none' : '90vh',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 0 60px rgba(0,0,0,0.5), 0 20px 40px rgba(0,0,0,0.3)',
                  // Safari-specific fixes
                  WebkitOverflowScrolling: 'touch',
                  height: isMobile ? 'auto' : '90vh'
                }}
              >
                {/* Screen Content */}
                <div className="min-h-full flex flex-col relative">
                  
                  {/* Status Bar */}
                  <div className="h-8 flex items-center justify-center pt-2">
                    <div className="w-12 h-1 bg-white/30 rounded-full"></div>
                  </div>

                  {/* Connection/Microphone Status - Only show if there's an issue */}
                  {(connectionStatus !== 'connected' || !microphoneEnabled) && (
                    <div className="absolute top-12 left-4 right-4 z-10">
                      <div className="bg-red-500/20 backdrop-blur-md rounded-2xl p-3 border border-red-500/30">
                        <div className="text-red-200 text-sm text-center">
                          {connectionStatus !== 'connected' && (
                            <div>Connection: {getStatusText()}</div>
                          )}
                          {!microphoneEnabled && (
                            <div>Microphone access required</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Phone Number Display */}
                  <div className="flex-1 flex flex-col justify-center px-8 pb-4">
                    
                    {/* Country Selection */}
                    <div className="text-center mb-6">
                      <button
                        type="button"
                        onClick={() => setShowCountryModal(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200"
                      >
                        <CountryFlag countryCode={selectedCountry.code} size="sm" />
                        <span className="text-sm">{selectedCountry.name}</span>
                        <span className="text-xs text-white/60">{selectedCountry.dialingCode}</span>
                      </button>
                    </div>

                    <div className="text-center">
                      <div className="text-white/60 text-sm mb-6 font-light">
                        {!phone ? 'Type a phone number to call' : ''}
                      </div>
                      <div className="text-white text-4xl font-light tracking-wider min-h-[3rem] flex items-center justify-center break-all px-4">
                        {phone ? (
                          <span>
                            <span className="text-white">{selectedCountry.dialingCode}</span>{phone}
                          </span>
                        ) : (
                          <span className="text-white/40">{selectedCountry.dialingCode}</span>
                        )}
                      </div>
                    </div>

                    {/* Call Status */}
                    {isConnecting && (
                      <div className="text-center text-blue-400 text-lg mt-6 font-light">
                        Connecting...
                      </div>
                    )}
                    
                    {isCalling && (
                      <div className="text-center mt-6">
                        <div className="text-white text-3xl font-mono mb-2">
                          {formatTime(timer)}
                        </div>
                        <div className="text-white/60 text-sm font-light">
                          Call in progress
                        </div>
                      </div>
                    )}

                    {/* Hangup Button - Only during call */}
                    {isCalling && (
                      <div className="flex justify-center mt-8">
                        <button
                          onClick={handleHangUp}
                          className="w-20 h-20 bg-red-500/90 backdrop-blur-xl rounded-full border border-red-400/30 flex items-center justify-center text-white text-2xl hover:bg-red-400/90 active:scale-95 transition-all duration-200"
                          style={{
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 20px rgba(200,0,0,0.3)'
                          }}
                        >
                          📞
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Liquid Glass Dialpad */}
                  <div className="px-6 pb-6">
                    
                    {/* Dialpad Grid - First 3 rows (1-9) */}
                    <div className="grid grid-cols-3 gap-6 mb-6 justify-items-center">
                      {dialPadNumbers.slice(0, 9).map((item) => (
                        <button
                          key={item.num}
                          onClick={() => addNumber(item.num)}
                          className="w-20 h-20 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex flex-col items-center justify-center text-white hover:bg-white/20 active:scale-95 transition-all duration-200"
                          style={{
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
                          }}
                        >
                          <span className="text-2xl font-light">{item.num}</span>
                          {item.letters && (
                            <span className="text-xs text-white/60 mt-1 tracking-widest">{item.letters}</span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Bottom Row: *, 0, # - Perfectly aligned */}
                    <div className="grid grid-cols-3 gap-6 mb-8 justify-items-center">
                      <button
                        onClick={() => addNumber('*')}
                        className="w-20 h-20 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex items-center justify-center text-white text-2xl hover:bg-white/20 active:scale-95 transition-all duration-200"
                        style={{
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
                        }}
                      >
                        *
                      </button>
                      <button
                        onClick={() => addNumber('0')}
                        className="w-20 h-20 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex flex-col items-center justify-center text-white hover:bg-white/20 active:scale-95 transition-all duration-200"
                        style={{
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
                        }}
                      >
                        <span className="text-2xl font-light">0</span>
                        <span className="text-xs text-white/60 mt-1">+</span>
                      </button>
                      <button
                        onClick={() => addNumber('#')}
                        className="w-20 h-20 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex items-center justify-center text-white text-2xl hover:bg-white/20 active:scale-95 transition-all duration-200"
                        style={{
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
                        }}
                      >
                        #
                      </button>
                    </div>

                    {/* Action Buttons - Perfectly centered */}
                    <div className="flex justify-center items-center">
                      <div className="flex items-center space-x-12">
                        {/* Backspace Button - Only when not calling and has numbers */}
                        {!isCalling && phone ? (
                          <button
                            onClick={removeLastDigit}
                            className="w-16 h-16 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 flex items-center justify-center text-white text-xl hover:bg-white/20 active:scale-95 transition-all duration-200"
                            style={{
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'
                            }}
                          >
                            ⌫
                          </button>
                        ) : (
                          <div className="w-16"></div>
                        )}

                        {/* Call Button - Always centered */}
                        {!isCalling && !isConnecting ? (
                          <button
                            onClick={(e) => {
                              console.log('🎯 Call button clicked!', {
                                phone,
                                connectionStatus,
                                isBlockedNumber: isBlockedNumber(phone || ''),
                                disabled: !phone || connectionStatus !== 'connected' || isBlockedNumber(phone || ''),
                                buttonElement: e.target
                              });
                              handleCall();
                            }}
                            disabled={!phone || connectionStatus !== 'connected' || isBlockedNumber(phone)}
                            className="w-20 h-20 bg-green-500/90 backdrop-blur-xl rounded-full border border-green-400/30 flex items-center justify-center text-white text-2xl hover:bg-green-400/90 disabled:bg-gray-500/50 disabled:cursor-not-allowed active:scale-95 transition-all duration-200"
                            style={{
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 20px rgba(0,200,0,0.3)'
                            }}
                          >
                            📞
                          </button>
                        ) : (
                          <button
                            onClick={handleHangUp}
                            className="w-20 h-20 bg-red-500/90 backdrop-blur-xl rounded-full border border-red-400/30 flex items-center justify-center text-white text-2xl hover:bg-red-400/90 active:scale-95 transition-all duration-200"
                            style={{
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 20px rgba(200,0,0,0.3)'
                            }}
                          >
                            📞
                          </button>
                        )}

                        {/* Right spacer */}
                        <div className="w-16"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Minimalist Call Log - Desktop Only */}
              <div className="hidden lg:block w-80 h-[90vh] max-h-[800px]">
                <div 
                  className="h-full bg-black/20 backdrop-blur-2xl rounded-[2.5rem] border border-white/10 shadow-2xl overflow-hidden"
                  style={{
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 0 40px rgba(0,0,0,0.4), 0 20px 30px rgba(0,0,0,0.2)'
                  }}
                >
                  {/* Header */}
                  <div className="p-6 border-b border-white/5">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-white text-xl font-light tracking-wide">Recent Calls</h3>
                        <p className="text-white/50 text-sm font-light mt-0.5">Tap to call back</p>
                      </div>
                    </div>
                  </div>

                  {/* Call Log List */}
                  <div className="h-full pb-20 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/10 hover:scrollbar-thumb-white/20">
                    {loadingHistory ? (
                      <div className="flex items-center justify-center p-12">
                        <div className="flex flex-col items-center space-y-4">
                          <div className="w-6 h-6 border border-white/30 border-t-blue-400 rounded-full animate-spin"></div>
                          <div className="text-white/60 text-sm font-light">Loading calls...</div>
                        </div>
                      </div>
                    ) : callHistory.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center mb-4">
                          <svg className="w-8 h-8 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <div className="text-white/60 text-lg font-light mb-2">No calls yet</div>
                        <div className="text-white/40 text-sm font-light max-w-xs leading-relaxed">Make your first call to see your call history here</div>
                      </div>
                    ) : (
                      <div className="p-4 space-y-3">
                        {callHistory.slice(0, 10).map((call, index) => (
                          <button
                            key={call.id}
                            onClick={() => handleCallHistoryClick(call.destinationNumber)}
                            className="w-full p-4 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 hover:border-white/20 hover:bg-white/10 active:scale-[0.98] transition-all duration-300 text-left group"
                            style={{
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 2px 10px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                {/* Phone Number */}
                                <div className="text-white font-light text-lg tracking-wide group-hover:text-blue-300 transition-colors duration-200 truncate mb-1">
                                  {call.destinationNumber?.startsWith('+1') 
                                    ? call.destinationNumber 
                                    : `+1${call.destinationNumber}`}
                                </div>
                                
                                {/* Call Info Row */}
                                <div className="flex items-center space-x-3 text-sm">
                                  {/* Status with colored dot */}
                                  <div className="flex items-center space-x-1.5">
                                    <div className={`w-1.5 h-1.5 rounded-full ${
                                      call.callStatus === 'completed' || call.callStatus === 'answered' ? 'bg-green-400' :
                                      call.callStatus === 'failed' ? 'bg-red-400' :
                                      call.callStatus === 'busy' ? 'bg-yellow-400' :
                                      call.callStatus === 'no-answer' ? 'bg-orange-400' :
                                      'bg-gray-400'
                                    }`}></div>
                                    <span className={`text-xs font-medium ${getCallStatusColor(call.callStatus)}`}>
                                      {getCallStatusText(call.callStatus)}
                                    </span>
                                  </div>
                                  
                                  {/* Duration */}
                                  <span className="text-white/60 text-xs">•</span>
                                  <span className="text-white/70 text-xs font-mono tabular-nums">
                                    {formatDuration(call.duration)}
                                  </span>
                                </div>
                                
                                {/* Time */}
                                <div className="text-white/40 text-xs font-light mt-2">
                                  {call.startTime ? new Intl.DateTimeFormat('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  }).format(call.startTime) : 'Recently'}
                                </div>
                              </div>
                              
                              {/* Contact and Call Icons */}
                              <div className="ml-3 flex gap-2">
                                {/* Contact Icon */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToContact(call.destinationNumber);
                                  }}
                                  className="w-8 h-8 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/30 hover:text-green-400 hover:bg-green-500/10 hover:border-green-400/20 transition-all duration-200"
                                  title="View in Contacts"
                                >
                                  <MdContacts className="w-4 h-4" />
                                </button>
                                
                                {/* Call Action Icon */}
                                <div className="w-8 h-8 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/30 group-hover:text-blue-400 group-hover:bg-blue-500/10 group-hover:border-blue-400/20 transition-all duration-200">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                        
                        {/* Show more indicator if there are more calls */}
                        {callHistory.length > 10 && (
                          <div className="text-center pt-2 pb-2">
                            <div className="text-white/40 text-xs font-light">
                              +{callHistory.length - 10} more calls
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-800 text-green-100 p-4 rounded-lg border border-green-600 max-w-md">
          <p className="font-medium">{successMessage}</p>
        </div>
      )}

      {/* Messaging Dialog */}
      {showMessagingDialog && (
        <div className="fixed inset-0 bg-gray-900/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-4">Upgrade to Premium + Messaging</h3>
            <p className="text-gray-300 mb-6">
              World Call requires Premium + Messaging subscription. To make international calls, activate Premium + Messaging for +$3/mo.
            </p>
            <div className="flex justify-between">
              <button
                onClick={() => setShowMessagingDialog(false)}
                className="rounded-md bg-gray-700 px-3 py-2 text-sm font-medium text-white hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={activateMessaging}
                disabled={isActivatingMessaging}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isActivatingMessaging ? 'Activating...' : 'Activate Premium + Messaging'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* International Country Selection Modal */}
      <InternationalCountryModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        onSelectCountry={handleCountrySelect}
      />
    </>
  );
} 